import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const ACTION_TONE = {
  "auth.login.ok":       "bg-emerald-50 text-emerald-800 border-emerald-200",
  "auth.login.fail":     "bg-red-50 text-red-800 border-red-200",
  "auth.login.otp_fail": "bg-red-50 text-red-800 border-red-200",
  "totp.enable":         "bg-sky-50 text-sky-800 border-sky-200",
  "totp.disable":        "bg-amber-50 text-amber-800 border-amber-200",
  "totp.setup":          "bg-slate-50 text-slate-700 border-slate-200",
  "approval.approved":   "bg-emerald-50 text-emerald-800 border-emerald-200",
  "approval.rejected":   "bg-red-50 text-red-800 border-red-200",
  "approval_rule.set":   "bg-sky-50 text-sky-800 border-sky-200",
  "rbac.update":         "bg-purple-50 text-purple-800 border-purple-200",
};

/**
 * Immutable audit log viewer — admin-only. Filters by action, resource,
 * or actor email. Rows are strictly append-only on the backend; no edit/delete
 * exposed to the UI or API.
 */
export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ action: "", resource: "", actor_email: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.action) params.action = filters.action;
      if (filters.resource) params.resource = filters.resource;
      if (filters.actor_email) params.actor_email = filters.actor_email;
      const [r1, r2] = await Promise.all([
        api.get("/audit-log", { params }),
        api.get("/audit-log/actions"),
      ]);
      setRows(r1.data);
      setActions(r2.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="audit-log-page">
      <PageHeader
        title="Audit Log"
        subtitle="Immutable, append-only ledger of every security & data event."
        module="Security"
        actions={
          <button
            onClick={load}
            disabled={loading}
            data-testid="refresh-audit"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold border border-slate-300 rounded-sm hover:bg-slate-50"
          >
            <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <div className="label-caps mb-1">Action</div>
            <Select value={filters.action || "__any__"} onValueChange={(v) => setFilters({ ...filters, action: v === "__any__" ? "" : v })}>
              <SelectTrigger data-testid="filter-action"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any action</SelectItem>
                {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-caps mb-1">Resource</div>
            <Input
              placeholder="e.g. invoice"
              value={filters.resource}
              data-testid="filter-resource"
              onChange={(e) => setFilters({ ...filters, resource: e.target.value })}
            />
          </div>
          <div>
            <div className="label-caps mb-1">Actor email</div>
            <Input
              placeholder="e.g. admin@…"
              value={filters.actor_email}
              data-testid="filter-actor"
              onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })}
            />
          </div>
          <div className="flex items-end">
            <div className="text-[11px] text-slate-500">
              <Shield size={12} className="inline mb-0.5 mr-1" />
              {rows.length} events · newest first
            </div>
          </div>
        </div>

        {rows.length === 0 ? (
          <EmptyState title="No audit events match" description="Adjust filters or perform an action to populate the log." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="audit-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">When</TableHead>
                  <TableHead className="label-caps">Actor</TableHead>
                  <TableHead className="label-caps">Action</TableHead>
                  <TableHead className="label-caps">Resource</TableHead>
                  <TableHead className="label-caps">IP</TableHead>
                  <TableHead className="label-caps">Status</TableHead>
                  <TableHead className="label-caps">Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                    <TableCell className="text-[12px] text-slate-600 whitespace-nowrap">{new Date(r.at).toLocaleString()}</TableCell>
                    <TableCell className="text-[12px] text-slate-700">{r.actor_email || "—"}</TableCell>
                    <TableCell>
                      <span className={`inline-block text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm border ${ACTION_TONE[r.action] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {r.action}
                      </span>
                    </TableCell>
                    <TableCell className="text-[12px] text-slate-600">
                      {r.resource}
                      {r.resource_id && <div className="text-[10px] font-mono text-slate-400 truncate max-w-[160px]">{r.resource_id}</div>}
                    </TableCell>
                    <TableCell className="text-[11px] font-mono text-slate-500">{r.ip || "—"}</TableCell>
                    <TableCell>
                      <span className={`text-[10px] uppercase tracking-widest font-semibold ${r.status === "error" ? "text-red-600" : "text-emerald-600"}`}>
                        {r.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-[11px] text-slate-500 max-w-[260px]">
                      {r.detail && Object.keys(r.detail).length > 0 && (
                        <code className="font-mono text-[10.5px] break-all">{JSON.stringify(r.detail)}</code>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
